import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Color, Container, FillGradient, Sprite, Text, TextStyle } from "pixi.js";
import { Easing, Group, Tween } from "tweedle.js";
import { globalScreen, languageGlobal } from "../config/GlobalVariableConfig";
import { getFontConfig } from "../config/introTextConfig";
import { isFireFox } from "../utils";

declare const window: any;
const translationManager = window.hacksawCasino.Translation;

// ─────────────────────────────────────────────────────────────────────────────
//  TIMING CONSTANTS  (all values in milliseconds — tweak freely)
// ─────────────────────────────────────────────────────────────────────────────
const TIMING = {
    // ── Phase 0 ──────────────────────────────────────────────────────────────
    initialDelay: 150,   // pause before anything moves

    // ── Phase 1 : Tile shell drop ────────────────────────────────────────────
    tileDropDuration: 340,   // how long each tile falls
    tileStagedDelay: 0,   // gap between successive tiles dropping
    shakeStep: 45,    // duration of each shake oscillation frame

    // ── Phase 2 : Logo slam ───────────────────────────────────────────────────
    logoDropDuration: 270,   // logo fall time
    logoPunchStep: 85,    // each segment of the scale-punch

    // ── Phase 3a/c : Heading & description pop-in ────────────────────────────
    headingPopDuration: 180,   // scale 0→1.15 + fade
    headingPopSettle: 80,    // scale 1.15→1.0
    headingStagger: 80,    // delay between tile 1 → 2 → 3
    descPopDuration: 180,
    descPopSettle: 80,
    descStagger: 80,

    // ── Phase 3b : Image fade-in ─────────────────────────────────────────────
    imageFadeDuration: 280,
    imageStagger: 80,

    // ── Phase 4 : Volatility section ─────────────────────────────────────────
    volTextFadeDuration: 220,   // label fade-in
    volShootDuration: 200,   // each dot sliding to position
    volBouncePeak: 55,    // dot scale up on arrival
    volBounceSettle: 55,    // dot scale back to 1
    volSpriteStagedDelay: 50,    // gap between successive dots

    // ── Phase 5 : Continue button drop ───────────────────────────────────────
    continueDropDuration: 300,
    continueBounce1: 110,   // first rebound
    continueBounce2: 90,    // second rebound
    continueBounce3: 70,    // settling tap

    // ── Between phases ────────────────────────────────────────────────────────
    phaseGap: 160,
} as const;
// ─────────────────────────────────────────────────────────────────────────────

/** Inner display-object handles for one tile, used for the staged reveal. */
interface TileContent {
    heading: Text;
    image: Sprite;
    description: Text;
}

export class IntroScene extends Container {

    // ── Layout containers ────────────────────────────────────────────────────
    private allTextContainer: Container = new Container();
    private volatilityContainer: Container = new Container();
    private continueButtonContainer: Container = new Container();

    private tileContainer1 = new Container();
    private tileContainer2 = new Container();
    private tileContainer3 = new Container();

    // ── Spine / Sprites ──────────────────────────────────────────────────────
    private logo: Spine;
    private backgroundImage: Sprite;

    // ── Tween groups ─────────────────────────────────────────────────────────
    /** Portrait carousel tweens (unchanged from original). */
    private tweenGroupForTiles: Group = new Group();
    /** Kept for backwards-compatibility – was used by the old scale-in tween. */
    private tweenGroupForScale: Group = new Group();
    /** Continue button breathing pulse. */
    private tweenGroupForBreath: Group = new Group();
    /** All cinematic intro animations. */
    private tweenGroupForIntro: Group = new Group();

    // ── Portrait carousel state (unchanged) ──────────────────────────────────
    private tileIndex: number = 0;
    private animationInterval: any;
    private isAnimatingTiles = false;

    // ── Screen dimensions ─────────────────────────────────────────────────────
    private screenWidth: number = 0;
    private screenHeight: number = 0;

    // ── Intro animation state ─────────────────────────────────────────────────
    /**
     * True while the cinematic intro is running.
     * Guards `resize()` so it does not stomp tile/button positions mid-animation.
     */
    private introAnimating = false;

    /** Handles to each tile's inner elements, stored for staged reveal. */
    private tileContents: TileContent[] = [];

    /** Vol indicator sprites, stored for the sequential shoot-in. */
    private volSprites: Sprite[] = [];

    /** Volatility label ref — fades in before the dots. */
    private volatilityText: Text | null = null;

    // ─────────────────────────────────────────────────────────────────────────
    constructor() {
        super();
        this.eventMode = 'none';
        this.interactive = false;

        console.log("languageGlobal", languageGlobal);
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    override destroy(): void {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        super.destroy({ children: true, texture: false });
    }

    private handleVisibilityChange = (): void => {
        if (document.hidden) {
            this.endTileAnimation();
        } else {
            const isLandscape = this.screenWidth > this.screenHeight;
            if (!isLandscape) this.startTileAnimation();
        }
    };

    private animTileShellDropDelayed(
        tile: Container,
        targetX: number,
        delayMs: number
    ): Promise<void> {

        return new Promise(resolve => {

            setTimeout(() => {

                this.animTileShellDrop(tile, targetX)
                    .then(resolve);

            }, delayMs);

        });
    }

    private enableContinueInteraction(): void {

        this.eventMode = 'static';
        this.interactive = true;
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  SETUP
    // ═════════════════════════════════════════════════════════════════════════

    public async showInitLogo(screenWidth: number, screenHeight: number): Promise<void> {
        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        // ── Background ───────────────────────────────────────────────────────
        this.backgroundImage = Sprite.from('intro_bg');
        this.addChild(this.backgroundImage);
        this.addChild(this.allTextContainer);

        // ── Logo (invisible until Phase 2 slam) ──────────────────────────────
        this.logo = Spine.from({ skeleton: "introLogoSkel", atlas: "introLogoAtlas" });
        this.logo.state.setAnimation(0, "animation", true);
        this.logo.alpha = 0;
        this.logo.scale.set(1);
        this.allTextContainer.addChild(this.logo);

        // ── Volatility section (invisible until Phase 4) ──────────────────────
        this.setupVolatilityText();
        this.volatilityContainer.alpha = 0;

        // ── Tiles (shells visible; inner content hidden until Phase 3) ────────
        this.tileContents[0] = this.setupTile(
            this.tileContainer1, "image1",
            translationManager.game('intro_ticket_heading_1_uc'),
            translationManager.game('intro_ticket_desc_1'),
        );
        this.tileContents[1] = this.setupTile(
            this.tileContainer2, "image2",
            translationManager.game('intro_ticket_heading_2_uc'),
            translationManager.game('intro_ticket_desc_2'),
        );
        this.tileContents[2] = this.setupTile(
            this.tileContainer3, "image3",
            translationManager.game('intro_ticket_heading_3_uc'),
            translationManager.game('intro_ticket_desc_3'),
        );
        this.allTextContainer.addChild(this.tileContainer1);
        this.allTextContainer.addChild(this.tileContainer2);
        this.allTextContainer.addChild(this.tileContainer3);

        // ── Continue button (invisible until Phase 5) ─────────────────────────
        this.setupContinueButton();
        this.continueButtonContainer.alpha = 0;

        // ── Apply resize for background + container scale ONLY ────────────────
        // The intro guard prevents tile/button positions being overwritten.
        this.introAnimating = true;
        this.resize(screenWidth, screenHeight);

        // ── Push tiles off-screen above for the drop-in ───────────────────────
        const isLandscape = screenWidth > screenHeight;
        if (isLandscape) {
            this.tileContainer1.position.set(394, -600);
            this.tileContainer2.position.set(globalScreen / 2, -600);
            this.tileContainer3.position.set(1654, -600);
        } else {
            // Portrait: only tile 1 drops into view; 2 & 3 wait off-right
            this.tileContainer1.position.set(globalScreen / 2, -600);
            this.tileContainer2.position.set(globalScreen, 1090);
            this.tileContainer3.position.set(globalScreen, 1090);
        }

        // ── Kick off cinematic intro ──────────────────────────────────────────
        this.playIntroSequence();
    }

    // ─────────────────────────────────────────────────────────────────────────
    /**
     * Builds one tile, hides its inner elements, and returns handles to them
     * so the intro animation can reveal them in stages.
     */
    private setupTile(
        tileContainer: Container,
        tileImageName: string,
        headingTextVal: string,
        descTextVal: string,
    ): TileContent {

        // Animated ticket background
        const tileBase = Spine.from({ skeleton: "introTicketSkel", atlas: "introTicketAtlas" });
        tileBase.scale = 0.65;
        tileBase.state.setAnimation(0, "animation", true);
        tileContainer.addChild(tileBase);

        // ── Tile image (Phase 3b) ─────────────────────────────────────────────
        const tileImage = Sprite.from(tileImageName);
        tileImage.anchor.set(0.5);
        tileImage.position.set(0, 60);
        tileImage.scale = 0.8;
        tileImage.alpha = 1;                 // revealed in Phase 3b
        tileContainer.addChild(tileImage);

        // ── Heading (Phase 3a) ────────────────────────────────────────────────
        let text = "";

        if (["id-id", "de-de", "es-es", "el-gr", "da-dk", "vi-vn", "pt-br", "it-it", "tr-tr", "nl-nl", "ro-ro", "hr-hr"].includes(languageGlobal)) {
            text = "10.000x";
        } else if (["en-us", "es-pe", "ja-jp", "zh-cn", "zh-tw", "th-th", "ko-kr"].includes(languageGlobal)) {
            text = "10,000x";
        } else if (["fi-fi", "no-no", "pl-pl", "sv-se", "fr-fr", "lv-lv", "ru-ru", "bg-bg", "lt-lt", "hu-hu", "pt-pt", "cs-cz"].includes(languageGlobal)) {
            text = "10\u00A0000x";
        }
        if (tileImageName == "image3") {
            tileImage.scale.set(1);
            const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);
            const colors = ["#cd7323", "#be6916", "#d17a27", "#e59b3b", "#ffcc65", "#f6b64c"].map((color) => Color.shared.setValue(color).toNumber());

            colors.forEach((number, index) => {
                const ratio = index / colors.length;

                fill.addColorStop(ratio, number);
            });
            const maxWinText = new Text({
                text: text,
                style: {
                    fontFamily: "ArchivoBlack regular",
                    fill: { fill },
                    fontWeight: "100",
                    fontSize: 37,
                    // fontSize: 28,
                    stroke: {
                        color: "#9d0000",
                        width: 2,
                    },
                    dropShadow:
                    {
                        blur: 3,
                        color: "#fbebd2",
                        distance: 1,
                    },
                    letterSpacing: 1,//1,
                    wordWrap: true,
                    wordWrapWidth: 375,
                    align: "center",
                    lineHeight: 50,//50
                    breakWords: true,

                }
            });
            tileContainer.addChild(maxWinText);
            maxWinText.anchor.set(0.5);
            maxWinText.zIndex = 1;
            maxWinText.position.set(0, 50);
        }
        const {
            fontFamily: fhFamily, fontWeight: fhWeight,
            fontSize: fhSize, letterSpacing: fhLS,
            lineHeight: fhLH,
        } = getFontConfig(languageGlobal, "heading");

        const headingText = new Text({
            text: headingTextVal,
            style: {
                fontFamily: fhFamily,
                fontWeight: fhWeight,
                fontSize: fhSize,
                stroke: {
                    color: "#000000",
                    width: 2,
                    join: "round"
                },
                dropShadow: isFireFox() ? false :
                    {
                        blur: 1,
                        color: "#000000",
                        distance: 0,
                        alpha: 1
                    },
                letterSpacing: fhLS,
                wordWrap: true,
                wordWrapWidth: 400,
                align: "center",
                lineHeight: fhLH,
                breakWords: true,
            },
        });
        headingText.anchor.set(0.5);
        headingText.position.set(0, -90);
        headingText.alpha = 1;               // revealed in Phase 3a
        headingText.scale.set(1);
        tileContainer.addChild(headingText);

        // Gradient fill applied after measuring
        const hFill = new FillGradient(0, 0, 0, headingText.height);
        ["#ff544f", "#fad126"]
            .map(c => Color.shared.setValue(c).toNumber())
            .forEach((n, i, a) => hFill.addColorStop(i / a.length, n));
        headingText.style.fill = hFill;
        headingText.style.stroke = {
            color: "#d80000",
            width: 2,
        }

        // ── Description (Phase 3c) ────────────────────────────────────────────
        const {
            fontFamily: fdFamily, fontWeight: fdWeight,
            fontSize: fdSize, letterSpacing: fdLS,
            lineHeight: fdLH,
        } = getFontConfig(languageGlobal, "description");

        const descriptionText = new Text({
            text: descTextVal,
            style: new TextStyle({
                fontFamily: fdFamily,
                fill: "#efb684",
                fontWeight: fdWeight,
                fontSize: fdSize,
                align: "center",
                wordWrap: true,
                wordWrapWidth: 375,
                letterSpacing: fdLS,
                ...(fdLH != null && { lineHeight: fdLH }),
                breakWords: true,
            }),
        });
        descriptionText.anchor.set(0.5);
        if (tileImageName == "image3") {
            descriptionText.position.set(0, 220);
            tileImage.scale.set(1);
        } else {
            descriptionText.position.set(0, 240);
        }
        descriptionText.alpha = 1;           // revealed in Phase 3c
        descriptionText.scale.set(1);
        tileContainer.addChild(descriptionText);
        ["ru-ru"].forEach((lang) => {
            if (languageGlobal === lang) {
                headingText.style.fontSize = 33;
                descriptionText.style.fontSize = 26;
            }
        })
        while (descriptionText.height > 150) {
            descriptionText.style.fontSize -= 1;
        }

        return { heading: headingText, image: tileImage, description: descriptionText };
    }

    // ─────────────────────────────────────────────────────────────────────────
    private setupVolatilityText(): void {
        this.volatilityContainer.position.set(globalScreen / 2, 830);
        this.allTextContainer.addChild(this.volatilityContainer);

        const { fontFamily, fontWeight, fontSize } = getFontConfig(languageGlobal, "volatility");

        const text = new Text({
            text: translationManager.game('intro_volatility_uc'),
            style: {
                fontFamily, fontWeight, fontSize,
                stroke: {
                    color: "#7b3919",
                    width: 2,
                    join: "round"
                },
                dropShadow: isFireFox() ? false :
                    {
                        blur: 1,
                        color: "#000000",
                        distance: 8,
                        alpha: 1
                    },
            },
        });
        text.anchor.set(0.5);
        text.position.set(-160, 0);
        // text.scale.set(Math.min(500 / text.width, 1));
        text.alpha = 0;                      // fades in during Phase 4
        while (text.width > 280) {
            text.style.fontSize -= 1;
        }
        const tFill = new FillGradient(0, 0, 0, text.height);
        ["#ffffff", "#decfc4", "#c5936b", "#c6936b"]
            .map(c => Color.shared.setValue(c).toNumber())
            .forEach((n, i, a) => tFill.addColorStop(i / a.length, n));
        text.style.fill = tFill;

        this.volatilityText = text;
        this.volatilityContainer.addChild(text);

        // Vol indicator dots
        const volatilityVal = 3;
        const volSpacing = 58;
        const volStartX = -104 + text.width / 2;
        this.volSprites = [];

        for (let i = 0; i < 5; i++) {
            const vol = Sprite.from(i < volatilityVal ? 'volatilityOn' : 'volatilityOff');
            vol.anchor.set(0.5);
            vol.position.set(volStartX + i * volSpacing, 0);
            vol.alpha = 0;                   // shot in one by one during Phase 4
            this.volSprites.push(vol);
            this.volatilityContainer.addChild(vol);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    private setupContinueButton(): void {
        this.continueButtonContainer = new Container();
        this.allTextContainer.addChild(this.continueButtonContainer);

        // const base = Sprite.from('continueBase');
        // base.anchor.set(0.5);
        // base.scale.set(0.85, 0.9);
        // this.continueButtonContainer.addChild(base);

        const { fontFamily, fontWeight, fontSize } = getFontConfig(languageGlobal, "continue");

        const text = new Text({
            text: translationManager.game('intro_continue_uc'),
            style: {
                fontFamily, fontWeight, fontSize,
                stroke: {
                    color: "#7b3919",
                    width: 2,
                    join: "round"
                },
                dropShadow: isFireFox() ? false :
                    {
                        blur: 1,
                        color: "#000000",
                        distance: 8,
                        alpha: 1
                    },
            },
        });
        text.anchor.set(0.5);
        const btnScale = Math.min(550 / text.width, 1);
        text.scale.set(btnScale);

        const bFill = new FillGradient(0, 0, 0, text.height);
        ["#ffffff", "#decfc4", "#c5936b", "#c6936b"]
            .map(c => Color.shared.setValue(c).toNumber())
            .forEach((n, i, a) => bFill.addColorStop(i / a.length, n));
        text.style.fill = bFill;

        text.position.set(0, -5);
        this.continueButtonContainer.addChild(text);

        // Breathing pulse — always running; button's own alpha gates visibility
        new Tween(text.scale, this.tweenGroupForBreath)
            .to({ x: btnScale * 1.03, y: btnScale * 1.03 }, 1000)
            .yoyo(true)
            .repeat(Infinity)
            .start();

        ["bg-bg", "ru-ru", "lv-lv", "cs-cz"].forEach(lang => {
            if (languageGlobal === lang) text.style.fontSize = 36;
        });
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  INTRO ANIMATION SEQUENCE
    // ═════════════════════════════════════════════════════════════════════════

    /**
     * Runs the 5-phase cinematic intro sequence.
     * Uses async/await + Promise-wrapped tweens so each phase waits for the
     * previous one to fully complete before starting.
     */
    private playIntroSequence(): void {
        const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
        const isLandscape = this.screenWidth > this.screenHeight;

        (async () => {
            await delay(TIMING.initialDelay);

            // ─── Phase 1 : Tile shells drop one by one ──────────────────────
            if (isLandscape) {

                this.animTileShellDropDelayed(this.tileContainer1, 394, 0);
                this.animTileShellDropDelayed(this.tileContainer2, globalScreen / 2, 200);
                this.animTileShellDropDelayed(this.tileContainer3, 1654, 350);

            } else {
                // Portrait: only tile 1 drops; 2 & 3 will appear via carousel later
                this.animTileShellDrop(this.tileContainer1, globalScreen / 2);
            }
            // await delay(TIMING.phaseGap);

            // ─── Phase 2 : Logo slams in ─────────────────────────────────────
            this.animLogoSlam();
            // this.startTileImageIdleMotion();
            // await delay(TIMING.phaseGap);

            // // ─── Phase 3a : All headings pop in (staggered across tiles) ─────
            // const visibleTiles = isLandscape
            //     ? this.tileContents
            //     : [this.tileContents[0]];

            // await this.animStaggeredFadePop(
            //     visibleTiles.map(tc => tc.heading),
            //     TIMING.headingStagger,
            //     TIMING.headingPopDuration,
            //     TIMING.headingPopSettle,
            // );
            // await delay(TIMING.phaseGap);

            // // ─── Phase 3b : Images fade in ───────────────────────────────────
            // await this.animStaggeredFadeIn(
            //     visibleTiles.map(tc => tc.image),
            //     TIMING.imageStagger,
            //     TIMING.imageFadeDuration,
            // );
            // await delay(TIMING.phaseGap);

            // // ─── Phase 3c : Descriptions pop in ──────────────────────────────
            // await this.animStaggeredFadePop(
            //     visibleTiles.map(tc => tc.description),
            //     TIMING.descStagger,
            //     TIMING.descPopDuration,
            //     TIMING.descPopSettle,
            // );
            // await delay(TIMING.phaseGap);

            // ─── Phase 4 : Volatility label + dots shoot in ───────────────────
            await Promise.all([
                this.animVolatilitySection(),
                this.animContinueButtonDrop(),
            ]);
            // ─── Intro complete — hand back control to the normal resize system ─
            this.introAnimating = false;
            this.resize(this.screenWidth, this.screenHeight);
        })();
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  Tile image floating effect
    // ═════════════════════════════════════════════════════════════════════════

    // private startTileImageIdleMotion(): void {

    //     this.tileContents.forEach((tile, index) => {

    //         const image = tile.image;

    //         const baseY = image.y;
    //         const baseRot = image.rotation;

    //         // slight float
    //         new Tween(image, this.tweenGroupForBreath)
    //             .to(
    //                 {
    //                     y: baseY - 8,
    //                 },
    //                 1800 + (index * 200)
    //             )
    //             .yoyo(true)
    //             .repeat(Infinity)
    //             .easing(Easing.Sinusoidal.InOut)
    //             .start();

    //         // slight wind sway
    //         new Tween(image, this.tweenGroupForBreath)
    //             .to(
    //                 {
    //                     rotation: baseRot + 0.02,
    //                 },
    //                 2200 + (index * 150)
    //             )
    //             .yoyo(true)
    //             .repeat(Infinity)
    //             .easing(Easing.Sinusoidal.InOut)
    //             .start();

    //         // tiny breathing scale
    //         new Tween(image.scale, this.tweenGroupForBreath)
    //             .to(
    //                 {
    //                     x: 1.02,
    //                     y: 1.02,
    //                 },
    //                 2000 + (index * 120)
    //             )
    //             .yoyo(true)
    //             .repeat(Infinity)
    //             .easing(Easing.Sinusoidal.InOut)
    //             .start();
    //     });
    // }

    // ═════════════════════════════════════════════════════════════════════════
    //  PHASE ANIMATORS
    // ═════════════════════════════════════════════════════════════════════════

    /**
     * Phase 1 — drops one tile shell (no inner content) from off-screen top
     * to its target Y, then shakes it on impact.
     */
    private animTileShellDrop(tile: Container, targetX: number): Promise<void> {
        return new Promise<void>(resolve => {
            const targetY = 1090;
            tile.position.set(targetX, -600);

            new Tween(tile.position, this.tweenGroupForIntro)
                .to({ y: targetY }, TIMING.tileDropDuration)
                .easing(Easing.Quadratic.In)
                .onComplete(() => this.shakeContainer(tile, resolve))
                .start();
        });
    }

    /**
     * Phase 2 — logo drops in fast from above and does a scale-punch on landing
     * (squeeze-in → overshoot → settle).
     */
    private animLogoSlam(): Promise<void> {
        return new Promise<void>((resolve) => {

            const targetY = 650;

            this.logo.alpha = 1;

            // Start very high
            this.logo.position.x = globalScreen / 2;
            this.logo.position.y = targetY - 700;

            // Start stretched vertically slightly
            this.logo.scale.set(0.8, 1.4);

            new Tween(this.logo.position, this.tweenGroupForIntro)
                .to({ y: targetY + 40 }, 230)
                .easing(Easing.Quartic.In)
                .onComplete(() => {

                    // HARD impact squash
                    new Tween(this.logo.scale, this.tweenGroupForIntro)
                        .to({ x: 1.35, y: 0.72 }, 70)

                        .chain(

                            // rebound stretch
                            new Tween(this.logo.scale, this.tweenGroupForIntro)
                                .to({ x: 0.92, y: 1.15 }, 90)

                                .chain(

                                    // settle
                                    new Tween(this.logo.scale, this.tweenGroupForIntro)
                                        .to({ x: 1, y: 1 }, 120)
                                        .easing(Easing.Back.Out)
                                        .onComplete(() => resolve())
                                )
                        )
                        .start();

                    // position rebound
                    new Tween(this.logo.position, this.tweenGroupForIntro)
                        .to({ y: targetY - 18 }, 90)
                        .chain(
                            new Tween(this.logo.position, this.tweenGroupForIntro)
                                .to({ y: targetY }, 110)
                        )
                        .start();
                })
                .start();
        });
    }

    /**
     * Phase 3a / 3c — scales and fades in an array of elements, staggered.
     * Each element: scale 0 → 1.15 (with simultaneous fade), then 1.15 → 1.0.
     * Resolves once the last element's animation finishes.
     */
    // private animStaggeredFadePop(
    //     targets: Array<Text | Sprite>,
    //     staggerMs: number,
    //     popDuration: number,
    //     settleDuration: number,
    // ): Promise<void> {
    //     return new Promise<void>(resolve => {
    //         let completed = 0;
    //         targets.forEach((target, i) => {
    //             setTimeout(() => {
    //                 this.singleFadePop(target, popDuration, settleDuration, () => {
    //                     completed++;
    //                     if (completed === targets.length) resolve();
    //                 });
    //             }, i * staggerMs);
    //         });
    //     });
    // }

    // private singleFadePop(
    //     target: Text | Sprite,
    //     popDuration: number,
    //     settleDuration: number,
    //     onComplete?: () => void,
    // ): void {
    //     target.alpha = 0;
    //     target.scale.set(0);

    //     // Proxy object so we can drive both alpha and scale via one tween
    //     const proxy = { alpha: 0, scale: 0 };

    //     new Tween(proxy, this.tweenGroupForIntro)
    //         .to({ alpha: 1, scale: 1.15 }, popDuration)
    //         .onUpdate(() => {
    //             target.alpha = proxy.alpha;
    //             target.scale.set(proxy.scale);
    //         })
    //         .chain(
    //             new Tween(proxy, this.tweenGroupForIntro)
    //                 .to({ scale: 1.0 }, settleDuration)
    //                 .onUpdate(() => target.scale.set(proxy.scale))
    //                 .onComplete(() => onComplete?.()),
    //         )
    //         .start();
    // }

    /**
     * Phase 3b — plain alpha fade-in for images, staggered.
     * Resolves once all images have faded in.
     */
    // private animStaggeredFadeIn(
    //     targets: Sprite[],
    //     staggerMs: number,
    //     fadeDuration: number,
    // ): Promise<void> {
    //     return new Promise<void>(resolve => {
    //         let completed = 0;
    //         targets.forEach((target, i) => {
    //             setTimeout(() => {
    //                 target.alpha = 0;
    //                 new Tween(target, this.tweenGroupForIntro)
    //                     .to({ alpha: 1 }, fadeDuration)
    //                     .onComplete(() => {
    //                         completed++;
    //                         if (completed === targets.length) resolve();
    //                     })
    //                     .start();
    //             }, i * staggerMs);
    //         });
    //     });
    // }

    /**
     * Phase 4 — reveals the volatility container, fades in the label, then
     * shoots each dot sprite from behind the label to its position one by one,
     * each with a small bounce on arrival.
     */
    private animVolatilitySection(): Promise<void> {

        return new Promise<void>((resolve) => {

            this.volatilityContainer.alpha = 1;

            if (!this.volatilityText) {
                resolve();
                return;
            }

            // ─────────────────────────────────────────────
            // TEXT SETUP
            // ─────────────────────────────────────────────
            this.volatilityText.alpha = 0;
            this.volatilityText.scale.set(0.7);

            const textProxy = {
                alpha: 0,
                scale: 0.7,
            };

            // ─────────────────────────────────────────────
            // VOL SPRITES SETUP
            // ─────────────────────────────────────────────
            this.volSprites.forEach((vol) => {
                vol.alpha = 0;
                vol.scale.set(0.2);
            });

            let completedSprites = 0;

            // Animate all dots simultaneously
            this.volSprites.forEach((vol) => {

                const proxy = {
                    alpha: 0,
                    scale: 0.2,
                };

                new Tween(proxy, this.tweenGroupForIntro)
                    .to({
                        alpha: 1,
                        scale: 1.25,
                    }, 160)

                    .easing(Easing.Back.Out)

                    .onUpdate(() => {
                        vol.alpha = proxy.alpha;
                        vol.scale.set(proxy.scale);
                    })

                    .chain(
                        new Tween(proxy, this.tweenGroupForIntro)
                            .to({
                                scale: 1,
                            }, 90)

                            .onUpdate(() => {
                                vol.scale.set(proxy.scale);
                            })

                            .onComplete(() => {

                                completedSprites++;

                                // Resolve only when ALL sprites finish
                                if (completedSprites === this.volSprites.length) {
                                    resolve();
                                }
                            })
                    )
                    .start();
            });

            // ─────────────────────────────────────────────
            // TEXT ANIMATION
            // ─────────────────────────────────────────────
            new Tween(textProxy, this.tweenGroupForIntro)
                .to({
                    alpha: 1,
                    scale: 1.18,
                }, 180)

                .easing(Easing.Back.Out)

                .onUpdate(() => {
                    this.volatilityText!.alpha = textProxy.alpha;
                    this.volatilityText!.scale.set(textProxy.scale);
                })

                .chain(
                    new Tween(textProxy, this.tweenGroupForIntro)
                        .to({
                            scale: 1,
                        }, 90)

                        .onUpdate(() => {
                            this.volatilityText!.scale.set(textProxy.scale);
                        })
                )
                .start();
        });
    }

    /**
     * Recursively shoots vol-dot sprites in sequence.
     * Each dot starts behind the label (at the label's X) and slides to its
     * final position, then bounces (scale up → scale back to 1).
     */
    private animVolSpriteChain(index: number, onAllDone: () => void): void {

        if (index >= this.volSprites.length) {
            onAllDone();
            return;
        }

        const vol = this.volSprites[index];

        vol.alpha = 0;
        vol.scale.set(0.2);

        const proxy = {
            alpha: 0,
            scale: 0.2,
        };

        new Tween(proxy, this.tweenGroupForIntro)
            .to({
                alpha: 1,
                scale: 1.25,
            }, 140)

            .easing(Easing.Back.Out)

            .onUpdate(() => {
                vol.alpha = proxy.alpha;
                vol.scale.set(proxy.scale);
            })

            .chain(
                new Tween(proxy, this.tweenGroupForIntro)
                    .to({
                        scale: 1,
                    }, 80)

                    .onUpdate(() => {
                        vol.scale.set(proxy.scale);
                    })

                    .onComplete(() => {

                        setTimeout(() => {
                            this.animVolSpriteChain(index + 1, onAllDone);
                        }, 50);

                    })
            )
            .start();
    }

    /**
     * Phase 5 — continue button drops from well above its target position
     * and settles with a three-stage bounce.
     */
    private animContinueButtonDrop(): Promise<void> {

        return new Promise<void>((resolve) => {

            const targetY = this.continueButtonContainer.y;

            this.continueButtonContainer.alpha = 0;

            this.continueButtonContainer.scale.set(0.4);

            this.continueButtonContainer.position.y = targetY + 120;

            const proxy = {
                alpha: 0,
                scale: 0.4,
                y: targetY + 120,
            };

            new Tween(proxy, this.tweenGroupForIntro)
                .to({
                    alpha: 1,
                    scale: 1.15,
                    y: targetY - 25,
                }, 240)

                .easing(Easing.Back.Out)

                .onUpdate(() => {
                    this.continueButtonContainer.alpha = proxy.alpha;
                    this.continueButtonContainer.scale.set(proxy.scale);
                    this.continueButtonContainer.position.y = proxy.y;
                })

                .chain(

                    new Tween(proxy, this.tweenGroupForIntro)
                        .to({
                            scale: 1,
                            y: targetY,
                        }, 120)

                        .onUpdate(() => {
                            this.continueButtonContainer.scale.set(proxy.scale);
                            this.continueButtonContainer.position.y = proxy.y;
                        })

                        .onComplete(() => {

                            // ENABLE INTERACTION HERE
                            this.enableContinueInteraction();

                            resolve();
                        })
                )
                .start();
        });
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  SHARED ANIMATION HELPERS
    // ═════════════════════════════════════════════════════════════════════════

    /**
     * Shakes a container horizontally (right → left → half-right → centre).
     * Runs on `tweenGroupForIntro` so it's updated automatically.
     */
    private shakeContainer(container: Container, onComplete?: () => void): void {
        const ox = container.x;
        const s = 10;                             // shake amplitude (px)
        const d = TIMING.shakeStep;               // duration per step

        new Tween(container.position, this.tweenGroupForIntro)
            .to({ x: ox + s }, d)
            .chain(
                new Tween(container.position, this.tweenGroupForIntro)
                    .to({ x: ox - s }, d)
                    .chain(
                        new Tween(container.position, this.tweenGroupForIntro)
                            .to({ x: ox + s * 0.5 }, d)
                            .chain(
                                new Tween(container.position, this.tweenGroupForIntro)
                                    .to({ x: ox }, d)
                                    .onComplete(() => {
                                        if (this.screenWidth > this.screenHeight) {
                                            this.landscapeTilePositions();
                                        } else {
                                            this.tileContainer1.position.set(globalScreen / 2, 1090);
                                            this.tileContainer2.position.set(globalScreen, 1090);
                                            this.tileContainer3.position.set(globalScreen, 1090);
                                            this.tileIndex = 0;
                                        }
                                        onComplete?.()
                                    }),
                            ),
                    ),
            )
            .start();
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  GAME-LOOP UPDATE
    // ═════════════════════════════════════════════════════════════════════════

    public update(): void {
        this.tweenGroupForScale.update();   // kept for backwards-compat
        this.tweenGroupForTiles.update();
        this.tweenGroupForBreath.update();
        this.tweenGroupForIntro.update();   // drives all cinematic intro tweens
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  RESIZE & LAYOUT  (original logic — guarded while intro is playing)
    // ═════════════════════════════════════════════════════════════════════════

    public resize(screenWidth: number, screenHeight: number, zoomFactor?: number): void {
        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;

        const isLandscape = screenWidth > screenHeight;
        let portraitscale: number;

        // Only reposition tiles / buttons / vol when the intro is NOT running.
        // During the intro, the cinematic system owns those positions.
        if (!this.introAnimating) {
            if (!isLandscape) {
                this.startTileAnimation();
                portraitscale = 1.75;

                this.logo.position.set(globalScreen / 2, 650);
                this.continueButtonContainer.scale.set(1);
                this.continueButtonContainer.position.set(globalScreen / 2, 1499);
                this.volatilityContainer.position.set(globalScreen / 2, 830);
                this.volatilityContainer.scale.set(1);
                this.tileContainer1.scale.set(1);
                this.tileContainer2.scale.set(1);
                this.tileContainer3.scale.set(1);
            } else {
                this.landscapeTilePositions();
                this.endTileAnimation();
                portraitscale = 1;

                this.logo.position.set(globalScreen / 2, 650);
                this.continueButtonContainer.scale.set(1);
                this.continueButtonContainer.position.set(globalScreen / 2, 1504);
                this.volatilityContainer.position.set(globalScreen / 2, 830);
            }
        } else {
            // Intro is running — just maintain the correct scale factor
            portraitscale = isLandscape ? 1 : 1.75;
        }

        const scale = portraitscale * (zoomFactor ?? 1)
            * Math.min(
                screenWidth / (isLandscape ? 1920 : 1080),
                screenHeight / (isLandscape ? 1080 : 1920),
            );

        this.allTextContainer.scale.set(scale);
        this.allTextContainer.x = -((globalScreen * scale - screenWidth) / 2);
        this.allTextContainer.y = -((globalScreen * scale - screenHeight) / 2);

        const bgScale = Math.max(scale, Math.max(screenWidth / 1920, screenHeight / 1920));
        this.backgroundImage.scale.set(bgScale);
        this.backgroundImage.position.set(
            -((1920 * bgScale - screenWidth) / 2),
            -((1920 * bgScale - screenHeight) / 2),
        );
    }

    // ═════════════════════════════════════════════════════════════════════════
    //  PORTRAIT TILE CAROUSEL  (original logic — fully preserved)
    // ═════════════════════════════════════════════════════════════════════════

    public endTileAnimation(): void {
        clearInterval(this.animationInterval);
        this.isAnimatingTiles = false;
    }

    private landscapeTilePositions(): void {
        clearInterval(this.animationInterval);
        this.tweenGroupForTiles.removeAll();

        this.tileContainer1.scale.set(1);
        this.tileContainer2.scale.set(1);
        this.tileContainer3.scale.set(1);
        this.tileContainer1.position.set(394, 1090);
        this.tileContainer2.position.set(globalScreen / 2, 1090);
        this.tileContainer3.position.set(1654, 1090);
    }

    private startTileAnimation(): void {
        if (this.isAnimatingTiles) return;
        this.isAnimatingTiles = true;
        this.resetTilePositions();
        clearInterval(this.animationInterval);
        this.animationInterval = setInterval(() => this.cycleTiles(), 3000);
    }

    private resetTilePositions(): void {
        this.tileContainer1.position.set(globalScreen / 2, 1090);
        this.tileContainer2.position.set(globalScreen, 1090);
        this.tileContainer3.position.set(globalScreen, 1090);
        this.tileIndex = 0;
        clearInterval(this.animationInterval);
    }

    private cycleTiles(): void {
        const tiles = [this.tileContainer1, this.tileContainer2, this.tileContainer3];
        const currentTile = tiles[this.tileIndex];
        const nextTile = tiles[(this.tileIndex + 1) % 3];

        new Tween(currentTile.position, this.tweenGroupForTiles)
            .to({ x: 0 }, 500)
            .start();

        new Tween(nextTile.position, this.tweenGroupForTiles)
            .to({ x: globalScreen / 2 }, 500)
            .onComplete(() => currentTile.position.set(globalScreen, 1090))
            .start();

        this.tileIndex = (this.tileIndex + 1) % 3;
    }
}